import { useCallback, useEffect, useRef } from 'react'

/**
 * CLUB VIP 富友 WPOS 扫码支付轮询 hook。
 * 三个页面 (BuyCards / ClubEcardsEquity / ClubVipChangeEcards) 共享同一轮询逻辑。
 *
 * @param {Object} options
 * @param {Function} options.checkStatus - 返回 boolean 的支付状态检查函数
 * @param {boolean} options.isActive - 支付弹窗是否可见 (nativePayDisplay)
 * @param {Function} options.onSuccess - 支付成功回调 (关闭弹窗、提示、刷新)
 * @param {Function} [options.onTimeout] - 支付超时回调 (关闭弹窗、提示)
 * @param {Function} [options.onPollingError] - 连续轮询失败达阈值时的提示回调（只提示一次，轮询继续，不判死）
 * @param {number} [options.timeoutMs] - 超时时间毫秒，默认 5 分钟
 */
export function useFuiouPaymentPolling({
  checkStatus,
  isActive,
  onSuccess,
  onTimeout,
  onPollingError,
  timeoutMs = 5 * 60 * 1000,
}) {
  const pollTimerRef = useRef(null)
  const pollTimeoutRef = useRef(null)
  const isPollingRef = useRef(false)
  const pollStatusRef = useRef(null)
  const mountedRef = useRef(true)
  const errorCountRef = useRef(0)
  // 轮询代数：每次 stop/start 递增，用于丢弃上一代在途请求的结果
  const pollEpochRef = useRef(0)

  // 连续失败提示阈值：10 次 × 3s ≈ 30s 网络全断才提示一次"仍在确认中"。
  // 达阈值不停止轮询——查询不到结果 ≠ 支付失败，用户可能已付款（网络中断时判死会诱导重复付款）；
  // 硬性退出只交给超时计时器，超时文案必须是中性"可能已完成"，绝不出现失败图标
  const MAX_CONSECUTIVE_ERRORS = 10

  // 停止轮询 & 清理
  const stopPolling = useCallback(() => {
    isPollingRef.current = false
    // 使在途请求结果失效：弹窗已关闭/超时/重启后，旧请求不得再触发 onSuccess/onPollingError
    pollEpochRef.current += 1
    errorCountRef.current = 0
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
  }, [])

  // 组件卸载时停止轮询 & 标记卸载
  useEffect(() => {
    // StrictMode 下 effect 会 setup→cleanup→setup 重跑：cleanup 置 false 后必须复位，
    // 否则重挂载后 mountedRef 恒 false，成功回调全部失效
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      stopPolling()
    }
  }, [stopPolling])

  // 弹窗关闭时停止轮询
  useEffect(() => {
    if (!isActive) {
      stopPolling()
    }
  }, [isActive, stopPolling])

  // 轮询支付状态（3s 间隔）
  // pollParams 直接透传给 checkStatus，不再包裹 { type } 避免双重嵌套
  const pollStatus = useCallback(
    async (pollParams) => {
      // 记录本次请求所属代数：期间若 stop/start（关弹窗、超时、重启），结果一律丢弃
      const epoch = pollEpochRef.current
      try {
        const result = await checkStatus(pollParams)
        // 旧代在途响应不得改写新一代的失败计数：先验代数再清零（跨代竞态）
        if (!isPollingRef.current || epoch !== pollEpochRef.current) return
        // 查询成功即网络通路正常，清零连续失败计数：
        // 失败判定只针对连续失败（≈30s 全断），间歇性抖动不会累积触发判死
        errorCountRef.current = 0
        // checkStatus 返回 boolean，直接判断即可
        if (result) {
          if (!isPollingRef.current || epoch !== pollEpochRef.current) return
          stopPolling()
          if (mountedRef.current) {
            onSuccess()
          }
          return
        }
      } catch (e) {
        if (!isPollingRef.current || epoch !== pollEpochRef.current) return
        errorCountRef.current += 1
        // 达阈值只提示一次"网络异常仍在确认"，不停止轮询：
        // 轮询失败/超时是"查不到结果"而非"支付失败"，判死关闭弹窗会让已付款用户误以为
        // 未支付而重复下单付款（二次扣款）。硬退出只交给超时计时器（中性文案）。
        if (errorCountRef.current === MAX_CONSECUTIVE_ERRORS && mountedRef.current) {
          if (onPollingError) {
            onPollingError()
          } else {
            console.warn('useFuiouPaymentPolling: 连续轮询失败达到提示阈值，但未提供 onPollingError 回调')
          }
        }
      }
      // 仍在同一代轮询中才续期，避免旧链与重启后的新链并行
      if (isPollingRef.current && epoch === pollEpochRef.current) {
        pollTimerRef.current = setTimeout(() => pollStatusRef.current(pollParams), 3000)
      }
    },
    [checkStatus, stopPolling, onSuccess, onPollingError]
  )
  pollStatusRef.current = pollStatus

  // 开始轮询，pollParams 直接透传给 checkStatus
  const startPolling = useCallback(
    (pollParams) => {
      // 幂等兜底：若已有轮询在跑，先停掉旧的再启新的，避免双轮询
      if (isPollingRef.current) stopPolling()
      errorCountRef.current = 0
      isPollingRef.current = true
      // 启动超时计时器
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current)
      }
      pollTimeoutRef.current = setTimeout(() => {
        stopPolling()
        if (mountedRef.current) {
          if (onTimeout) {
            onTimeout()
          } else {
            console.warn('useFuiouPaymentPolling: 支付轮询超时，但未提供 onTimeout 回调')
          }
        }
      }, timeoutMs)
      pollStatus(pollParams)
    },
    [pollStatus, stopPolling, onTimeout, timeoutMs]
  )

  return { stopPolling, startPolling }
}
